import { formatCount } from './businessStatusFormat';

export type RingMode = 'share' | 'period';

interface RingChartProps {
  /**
   * `share`: the full ring is `total`, the highlighted arc is `highlight` (new in the last 30 days).
   * `period` (Orders, design D3): the highlighted arc is the last 30 days and the base arc the previous
   * 30 days, so the whole ring is `highlight + base`; the centre shows the last-30-days count.
   */
  mode: RingMode;
  highlight: number;
  /** `share`: the total. `period`: the previous-30-days count. */
  base: number;
  ariaLabel: string;
  /** `period` mode's centre caption, the selected period's span (#1331); "30 days" by default. */
  periodUnit?: string;
}

const R = 46;
const STROKE = 10;
/** Gap on each side of the highlighted arc so the two segments separate without colour (ux-notes). */
const GAP = 1.5;
/** A tiny share never draws thinner than this (the exact number is printed beside the ring). */
const MIN_ARC = 3;

/** Percentages (pathLength 100) of the highlighted and rest arcs. Exported for tests. */
export function ringArcs(mode: RingMode, highlight: number, base: number): { drawn: number; rest: number; empty: boolean } {
  const h = Math.max(0, highlight || 0);
  const whole = mode === 'period' ? h + Math.max(0, base || 0) : Math.max(0, base || 0, h);
  if (whole === 0) return { drawn: 0, rest: 100, empty: true };
  if (h === 0) return { drawn: 0, rest: 100, empty: false };
  if (h >= whole) return { drawn: 100, rest: 0, empty: false };
  const share = (h / whole) * 100;
  const drawn = Math.min(Math.max(share, MIN_ARC), 100 - MIN_ARC - GAP * 2);
  return { drawn: round2(drawn), rest: round2(Math.max(100 - drawn - GAP * 2, 0)), empty: false };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Spec 070 — inline SVG donut, no chart library. The wrapper is the accessible image; the SVG is hidden. */
export default function RingChart({ mode, highlight, base, ariaLabel, periodUnit = '30 days' }: RingChartProps) {
  const { drawn, rest, empty } = ringArcs(mode, highlight, base);
  const centre = mode === 'period' ? highlight : base;
  const unit = mode === 'period' ? periodUnit : 'total';
  const gapped = drawn > 0 && rest > 0;

  return (
    <div className={`bs-ring${empty ? ' bs-ring--empty' : ''}`} role="img" aria-label={ariaLabel} data-mode={mode}>
      <svg viewBox="0 0 104 104" aria-hidden="true" focusable="false">
        {rest > 0 && (
          <circle
            className="bs-ring__rest"
            cx="52" cy="52" r={R} fill="none" strokeWidth={STROKE} pathLength={100}
            strokeDasharray={`${rest} ${100 - rest}`}
            strokeDashoffset={gapped ? -(drawn + GAP) : 0}
          />
        )}
        {drawn > 0 && (
          <circle
            className="bs-ring__new"
            cx="52" cy="52" r={R} fill="none" strokeWidth={STROKE} pathLength={100}
            strokeDasharray={`${drawn} ${100 - drawn}`}
            data-testid="ring-highlight"
          />
        )}
      </svg>
      <div className="bs-ring__center" aria-hidden="true">
        <span className="bs-ring__total" data-testid="ring-centre">{formatCount(centre)}</span>
        <span className="bs-ring__unit">{unit}</span>
      </div>
    </div>
  );
}
